import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import {
  ICardElementWithChildren,
  IPeriod,
  IPeriodLevel,
} from "backend/model/model";
import getValues from "backend/values";
import PerformanceTrendChart from "design/components/charts/PerformanceTrendChart";
import useOriginalId from "qpr-base/apihooks/useOriginalId";
import { IChartSettings } from "qpr-base/configuration/types";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import Loader from "qpr-base/ui/components/Loader";
import React from "react";
import { useQuery } from "react-query";

const getChartPeriods = (
  element: ICardElementWithChildren | undefined,
  periodLevels: IPeriodLevel[] | undefined,
  settings: IChartSettings
): IPeriod[] => {
  function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
  }

  if (!element || !periodLevels) return [];

  const periods = periodLevels
    .filter((l) => l.id === (element.periodlevelid || element.periodlevel))
    .map((l) => l.periods)
    .filter(notEmpty)
    .pop();
  if (!periods) return [];
  const sortedPeriods = periods.sort((a, b) => {
    return a.startdate.localeCompare(b.startdate);
  });
  let currentIndex = 0;
  for (let i = 0; i < sortedPeriods.length; i += 1) {
    if (sortedPeriods[i].name === element.periodname) currentIndex = i;
  }
  const periodsBeforeActual = settings.periodsBeforeActual || 9;
  const periodsAfterActual = settings.periodsAfterActual || 2;

  const startIndex = Math.max(currentIndex - periodsBeforeActual, 0);
  const endIndex = Math.min(
    Number(currentIndex) + Number(periodsAfterActual),
    sortedPeriods.length - 1
  );
  const chartPeriods: IPeriod[] = [];
  for (let i = startIndex; i <= endIndex; i += 1)
    chartPeriods.push(sortedPeriods[i]);

  return chartPeriods;
};

const PerformanceTrend = (props: {
  disableAnimation?: boolean;
  element: ICardElementWithChildren;
  settings: IChartSettings;
  width?: string;
  showLegend?: boolean;
}) => {
  const ctx = useAppContext();
  const { data: originalId } = useOriginalId(props.element?.id || "");
  const { data: periodLevels } = usePeriodLevels();
  const { width } = props;

  const periods = getChartPeriods(props.element, periodLevels, props.settings);

  const { data: values } = useQuery(
    `trenddata-${props.element?.id}-${JSON.stringify(periods)}`,
    () => {
      return periods
        ? getValues(originalId || "", periods, false, periodLevels!)
        : [];
    },
    { enabled: !!originalId && !!periodLevels }
  );

  if (!values || values?.length === 0) {
    return <Loader />;
  }

  return (
    <div style={{ width: props.width || "720px" }}>
      <PerformanceTrendChart
        element={props.element}
        disableAnimation={props.disableAnimation}
        settings={props.settings}
        values={values}
        period={ctx.period || ""}
        width={width || "720px"}
        showLegend={props.showLegend}
      />
    </div>
  );
};

export default PerformanceTrend;
